/**
 * Which fund a live SignalR push belongs to (M3 Step 4, CIO decision 8, 2026-09-25).
 *
 * Every push on the `helion` hub (nav, intradayPnl, liveTape, riskDeltas) now carries a `fund` label from
 * the backend: the fund's public block ({ code, display_name, status, account: "****1234", ... }). The
 * dashboard shows Horizon (the fund of record) until the fund switch (M7), so a push labelled for ANOTHER
 * fund is ignored -- it can never be drawn under Horizon's numbers.
 *
 * Rules (pure; unit-tested in pushFund.test.ts and wired into useSignalR):
 *  - No `fund` field, or `fund: null`: a push from a backend that predates the label (or the HTTP shape),
 *    which has only ever been the fund of record's. Treated as Horizon, i.e. accepted while Horizon is shown.
 *  - `fund` as an object with a `code`, or as a plain code string: accepted only when it names the displayed
 *    fund (trimmed, case-insensitive).
 *  - `fund` present but unreadable (no code, a number, ...): ignored -- it cannot be confirmed as the
 *    displayed fund's.
 */

/** The fund every page shows until M7 adds the fund switch. */
export const DISPLAYED_FUND = "HORIZON";

/** The fund a push without a label belongs to: the fund of record. */
export const UNLABELLED_PUSH_FUND = "HORIZON";

function norm(v: unknown): string {
  return String(v ?? "").trim().toUpperCase();
}

/**
 * The fund code a push is labelled with: a code string; `null` when it carries no label (no `fund` field,
 * or `fund: null`); `undefined` when the label is present but unreadable.
 */
export function pushFundCode(payload: unknown): string | null | undefined {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
  if (!("fund" in (payload as Record<string, unknown>))) return null;
  const f = (payload as Record<string, unknown>).fund;
  // `fund: null` is read as "no label" (= the fund of record's push) ON PURPOSE, for backwards compatibility:
  // a pre-Step-4 intradayPnl payload sends `fund: null` when the endpoint cannot attribute its figures. The
  // Step 4 backend never pushes such a payload (strategy/broadcast_fund.py withholds it). Revisit with the
  // fund switch (M7, review 2026-10-06): once every push is labelled, `fund: null` can be refused.
  if (f === null || f === undefined) return null;
  if (typeof f === "string") return norm(f) || undefined;
  if (typeof f === "object" && !Array.isArray(f)) {
    const code = norm((f as Record<string, unknown>).code);
    return code || undefined;
  }
  return undefined;
}

/** True when a push may be shown on a page displaying `displayedFund` (Horizon until M7). */
export function acceptPushForFund(payload: unknown, displayedFund: string = DISPLAYED_FUND): boolean {
  const shown = norm(displayedFund);
  const code = pushFundCode(payload);
  if (code === undefined) return false;
  if (code === null) return shown === UNLABELLED_PUSH_FUND;
  return code === shown;
}
